"use client";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Chill() {
  const {
    ref: badgeRef,
    isVisible: badgeVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: subRef,
    isVisible: subVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="17962" id="share-the-chill" className="relative overflow-hidden bg-background text-foreground py-28 md:py-40">
      {}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-24 h-[60rem] w-[60rem] rounded-lg blur-3xl opacity-35" style={{
        background: "radial-gradient(circle at center, hsl(0 100% 71%) 0%, transparent 60%)",
        animation: "aurora-drift-1 24s ease-in-out infinite"
      }} />
        <div className="absolute top-40 -right-32 h-[55rem] w-[55rem] rounded-lg blur-3xl opacity-30" style={{
        background: "radial-gradient(circle at center, hsl(7 100% 75%) 0%, transparent 60%)",
        animation: "aurora-drift-2 24s ease-in-out infinite"
      }} />
        <div className="absolute bottom-0 left-1/3 h-[45rem] w-[45rem] rounded-lg blur-3xl opacity-25" style={{
        background: "radial-gradient(circle at center, hsl(355 100% 96%) 0%, transparent 60%)",
        animation: "aurora-drift-3 24s ease-in-out infinite"
      }} />
      </div>

      <style>{`
        @keyframes aurora-drift-1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(40px, -30px) rotate(3deg); }
        }
        @keyframes aurora-drift-2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-35px, 25px) rotate(-3deg); }
        }
        @keyframes aurora-drift-3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(25px, -20px) rotate(2deg); }
        }
        @keyframes crisp-in {
          from { filter: blur(8px); opacity: 0; transform: translateY(20px); }
          to { filter: blur(0); opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-start-2 md:col-end-12 lg:col-start-3 lg:col-end-11 flex flex-col items-center text-center">
            {}
            <div ref={badgeRef} className={`inline-flex items-center gap-2 rounded-lg border border-accent bg-accent backdrop-blur-xl px-3.5 py-1.5 shadow-sm transition-all duration-700 ${badgeVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              <span className="h-1.5 w-1.5 rounded-lg" style={{
              background: "var(--primary)"
            }} />
              <span className="text-xs font-medium tracking-wider text-primary uppercase">
                Artisanal &middot; Small Batch
              </span>
            </div>

            {}
            <h1 ref={headlineRef} className={`font-default mt-8 font-medium text-foreground transition-all duration-700 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(2.75rem, 5.8vw + 0.5rem, 5.5rem)",
            letterSpacing: "-0.03em",
            lineHeight: "1.02",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            transitionDelay: "80ms"
          }}>
              <span className="block">Pure artisanal</span>
              <span className="block font-serif font-normal italic" style={{
              color: "var(--primary)"
            }}>
                perfection
              </span>
            </h1>

            {}
            <p ref={subRef} className={`mt-7 max-w-[540px] font-default text-muted-foreground transition-all duration-700 ${subVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            fontSize: "clamp(1.0625rem, 0.4vw + 0.95rem, 1.25rem)",
            letterSpacing: "-0.005em",
            lineHeight: "1.55",
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            transitionDelay: "240ms"
          }}>
              Hand-churned ice cream crafted with heritage techniques. Experience 
              vibrant, natural flavors in every scoop, delivered straight to your door.
            </p>

            {}
            <div ref={ctaRef} className={`mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 transition-all duration-700 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
            transitionDelay: "400ms"
          }}>
              <Button asChild size="lg" className="px-8 py-6 text-base font-medium bg-primary text-primary-foreground hover:bg-foreground transition-all duration-200">
                <Link to="/ShopPage">Explore flavors</Link>
              </Button>
              <Button asChild size="lg" variant="secondary" className="px-8 py-6 text-base font-medium bg-transparent border border-foreground text-foreground hover:bg-foreground hover:text-white transition-all duration-200">
                <Link to="/TheArtisanalProcessPage">Our process</Link>
              </Button>
            </div>

            {}
            <div ref={imgRef} className="relative mt-16 md:mt-20 w-full flex justify-center md:justify-end">
              <div className="relative w-[70%] sm:w-[60%] md:w-[55%] lg:w-[50%] md:translate-x-8 lg:translate-x-16" style={{
              animation: imgVisible ? "crisp-in 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
              opacity: imgVisible ? undefined : 0
            }}>
                {}
                <div className="absolute inset-0 -z-10 blur-3xl opacity-50" style={{
                background: "radial-gradient(circle at center, hsl(0 100% 71%) 0%, transparent 65%)"
              }} />
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/scoop_signature_cone.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/scoop_signature_cone.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1709989/img/scoop_signature_cone.png-wvc-sizes" alt="Artisanal ice cream scoop in a crisp waffle cone" loading="lazy" decoding="async" className="w-full h-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}